import { fg } from '@atlaskit/platform-feature-flags';

import { AI_MATE_URL } from '../../common/constants';
import {
	type AssistanceServiceError,
	AssistanceServiceMessageTemplate,
	type AssistanceServiceMessageTemplateValues,
	type ConvoAiErrorPayload,
	type FetchIssueCreateDataProps,
	type SuggestionResult,
} from '../../common/types';

const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export class FetchSuggestionError extends Error {
	constructor(
		readonly errorMessage: string,
		readonly message_template: AssistanceServiceMessageTemplateValues,
	) {
		super(errorMessage);
	}
}
export class ConvoAiError extends Error {
	constructor(
		readonly statusCode: number,
		readonly errorMessage: string,
		readonly message_template: AssistanceServiceMessageTemplateValues,
		readonly shouldSlo: boolean = true,
	) {
		super(errorMessage);
	}
}

export const fetchAiSuggestedFields = async ({
	cloudId,
	confluencePage,
	highlightedText,
	signal,
	product = 'confluence',
	experienceId = 'ai-issue-create-confluence-single',
	overrideXProduct,
}: FetchIssueCreateDataProps): Promise<SuggestionResult | undefined> => {
	const xProduct = overrideXProduct ?? product;
	const response = await fetch(AI_MATE_URL, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Experience-Id': experienceId,
			'X-Product': xProduct,
			'Atl-CloudId': cloudId,
		},
		body: JSON.stringify({
			recipient_agent_named_id: 'ai_issue_create_agent',
			agent_input_context: {
				application: capitalizeFirstLetter(product),
				context: {
					confluence_page_markdown_plus: confluencePage,
					highlighted_text: highlightedText,
				},
				suggested_issues_config: {
					max_issues: 1,
					suggested_issue_field_types: [
						{
							issue_type: 'Task',
							fields: [
								{
									field_name: 'Summary',
									field_type: 'Short-Text',
								},
								{
									field_name: 'Description',
									field_type: 'Paragraph',
								},
							],
						},
					],
				},
			},
		}),
		credentials: 'include',
		signal,
	});

	if (!response.ok) {
		const errorPayload = await response.json();
		const error = handleErrorPayload(response.status, errorPayload);
		throw error;
	}

	const data = await response.json();
	const suggestion = parseData(data);
	if (!suggestion) {
		throw new Error(`Parsing response from ${experienceId} failed`);
	}
	return suggestion;
};

const NON_SLO_MESSAGE_TEMPLATES: AssistanceServiceMessageTemplateValues[] = [
	AssistanceServiceMessageTemplate.ACCEPTABLE_USE_VIOLATIONS,
	AssistanceServiceMessageTemplate.ACCEPTABLE_USE_EVALUATION_EXCEPTION,
	AssistanceServiceMessageTemplate.AI_DISABLED,
	AssistanceServiceMessageTemplate.CONVERSATION_CHANNEL_UNAUTHORIZED,
	AssistanceServiceMessageTemplate.EXCEEDING_CONTEXT_LENGTH_ERROR,
	AssistanceServiceMessageTemplate.EXPERIENCE_NOT_ENABLED_FOR_CUSTOMER_ACCOUNT,
	AssistanceServiceMessageTemplate.FEATURE_DISABLED,
	AssistanceServiceMessageTemplate.FEATURE_DISABLED_ON_SITE,
	AssistanceServiceMessageTemplate.HIPAA_CONTENT_DETECTED,
	AssistanceServiceMessageTemplate.OPENAI_RATE_LIMIT_USER_ABUSE,
	AssistanceServiceMessageTemplate.RATE_LIMIT,
];

const NON_SLO_STATUS_CODES = [403];

export const isWhitelistedMessateTemplate = (
	messageTemplate: AssistanceServiceMessageTemplateValues,
) => NON_SLO_MESSAGE_TEMPLATES.includes(messageTemplate);

export const isWhitelistedStatusCode = (status_code: number) =>
	NON_SLO_STATUS_CODES.includes(status_code);

export const parseData = (data: AssistanceServiceError | any): SuggestionResult | undefined => {
	let shouldSlo = fg('jira_ai_issue_create_status_code_whitelist')
		? !isWhitelistedStatusCode(data.status_code)
		: true;

	if ('message_template' in data) {
		// only check for whitelisted message templates if status code is not whitelisted
		if (shouldSlo) {
			shouldSlo = !isWhitelistedMessateTemplate(data.message_template);
		}

		throw new ConvoAiError(data.status_code, data.content, data.message_template, shouldSlo);
	}

	const content = data?.message?.content;
	if (!content) {
		return undefined;
	}

	try {
		const contentJson = JSON.parse(content);

		if (contentJson.suggested_issues === undefined) {
			throw new ConvoAiError(
				200,
				`Response suggested_issues is undefined. Key names: ${Object.keys(contentJson).toString()}`,
				AssistanceServiceMessageTemplate.NO_ANSWER_SUGGESTED_ISSUES,
				shouldSlo,
			);
		}

		if (contentJson.suggested_issues.length === 0) {
			throw new ConvoAiError(
				200,
				'No suggested issues are available for the context provided',
				AssistanceServiceMessageTemplate.NO_ANSWER_SUGGESTED_ISSUES,
				shouldSlo,
			);
		}
		const suggestion = {
			summary: contentJson.suggested_issues[0].field_values.Summary,
			description: contentJson.suggested_issues[0].field_values.Description,
		};
		const usage = data.usage_data.total;
		return { suggestion, usage };
	} catch (error: Error | unknown) {
		if (content.startsWith('_')) {
			const fixedJSON = {
				...data,
				message: {
					...data.message,
					content: content.slice(1),
				},
			};

			return parseData(fixedJSON);
		}

		if (error instanceof ConvoAiError) {
			throw error;
		}

		throw new ConvoAiError(
			200,
			`Failed to parse JSON: ${error}`,
			data.message_template ?? AssistanceServiceMessageTemplate.UNKNOWN_ERROR,
			shouldSlo,
		);
	}
};

export const handleErrorPayload = (
	statusCode: number,
	errorPayload: ConvoAiErrorPayload,
): ConvoAiError => {
	const { content, errorMessage, message_template } = errorPayload;
	// `content` is available when error is from assistance-service
	// `errorMessage` is available when error is from convo-ai (usually only AI enabled errors)
	// `message_template` is available when error is from assistance-service in case content is empty
	const message = content || (errorMessage ?? message_template);
	return new ConvoAiError(
		statusCode,
		message ?? 'Unknown error',
		message_template ?? AssistanceServiceMessageTemplate.UNKNOWN_ERROR,
	);
};
